import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { DefaultManager, ManagerProps } from '../components/Manager/Manager'
import { FEATURED_DISPLAY_TYPE, ICONS_ONLY } from './types'
import { FlowpageLinkCard } from '../../../components/flowpage/FlowpageLinkCard'
import { FlowpageProps } from '../types/FlowpageProps'
import { FormProps } from '../components/Layout'
import { SocialLinkFormV2 } from './SocialLinkFormV2'
import { SocialLinkIconCard } from '../../../components/flowpage/SocialLinkCard'
import { Stack, Typography } from '@dtx-company/design-system/src'
import { circleLogoUrlForProvider, featuredLogoUrlForProvider } from './SocialLinkFormV2/utils'
import { logoUrlForType } from '../../../utils/main'
import { processSocialUrl } from './helperFunctions/helperFunctions'
import { useMemo } from 'react'
import { useNewTabSlots } from '../../../hooks/useNewTabSlots'
import Image from 'next/image'

export const Form: React.FC<FormProps> = ({ order, curr, handleClose, widgetObj }: FormProps) => {
  return (
    <SocialLinkFormV2 order={order} curr={curr} handleClose={handleClose} widgetObj={widgetObj} />
  )
}

export const Flowpage: React.FC<FlowpageProps> = ({
  link,
  preview = false,
  editLinkMode = false
}: FlowpageProps) => {
  const resolvedActionData = link.actionData
  const resolvedChildLinks = useMemo(() => link.childLinks ?? [], [link.childLinks])
  const newTabSlot = useNewTabSlots()
  // childlinks do not exist on the link object until after they are first submitted
  const addingLink = (link.childLinks ?? []).length === 0
  const openLinksInSameTab = newTabSlot?.data?.sameTab
  //sort by order 0-10
  const sortedSocialProviderArray = useMemo(
    () =>
      !addingLink
        ? [...resolvedChildLinks]
            .sort((a, b) => a.order - b.order)
            .map(child => child?.provider ?? '')
        : Object.keys(resolvedActionData)
            .filter((adKey: string) => (resolvedActionData[adKey].order ?? -1) > -1)
            .sort(function (a, b) {
              return resolvedActionData[a].order - resolvedActionData[b].order
            }),
    [resolvedActionData, resolvedChildLinks, addingLink]
  )

  if (link.displayType === ICONS_ONLY) {
    return (
      <SocialLinkIconCard
        link={{
          ...link,
          actionData: link.actionData
        }}
        widgetId="socialLink"
        preview={preview}
        editLinkMode={editLinkMode}
        newTab={!openLinksInSameTab}
      />
    )
  } else {
    return (
      <Box flexDirection="column" width="100%">
        {sortedSocialProviderArray.map((socialLink: string, index) => {
          const targetChild = !addingLink
            ? resolvedChildLinks.find(child => child?.provider === socialLink)
            : resolvedActionData[socialLink]
          const childLinkId = !addingLink ? targetChild.id : link.id
          const featuredOrCardImageUrl =
            link.displayType === FEATURED_DISPLAY_TYPE
              ? featuredLogoUrlForProvider(socialLink)
              : logoUrlForType(socialLink)
          const iconSrc =
            targetChild.thumbNailImgUrl && targetChild.thumbNailImgUrl.length > 0
              ? targetChild.thumbNailImgUrl
              : featuredOrCardImageUrl
          return (
            <Box
              cursor="pointer"
              pb={index === sortedSocialProviderArray.length - 1 ? '0' : '20px'}
              width="100%"
              key={`${socialLink}-flowpage`}
            >
              <FlowpageLinkCard
                link={{
                  ...link,
                  id: childLinkId,
                  title: targetChild.title,
                  description: targetChild.description ?? '',
                  actionData: {
                    ...targetChild,
                    link: processSocialUrl(
                      !addingLink ? targetChild.actionData.link : targetChild.link,
                      socialLink
                    )
                  }
                }}
                widgetId="socialLink"
                preview={preview}
                editLinkMode={editLinkMode}
                iconSrc={iconSrc}
                parentLinkId={!addingLink ? link.id : undefined}
              />
            </Box>
          )
        })}
      </Box>
    )
  }
}

export const Manager: React.FC<ManagerProps> = ({ link, editWidget, handle }: ManagerProps) => {
  const ad = link.actionData
  const adArray = useMemo(
    () =>
      Object.keys(ad)
        .filter((adKey: string) => (ad[adKey].order ?? -1) > -1)
        .sort(function (a, b) {
          return ad[a].order - ad[b].order
        }),
    [ad]
  )
  return (
    <DefaultManager
      handle={handle}
      editWidget={editWidget}
      link={link}
      iconUrl="/icons/social-media/social-link-smile.svg"
      customTitleSection={
        <Stack gap={2} sx={{ width: '350px' }}>
          <Typography variant="h4">Socials</Typography>
          <Stack direction="row" gap={2} flexWrap="wrap">
            {adArray.map((provider: string) => {
              return (
                <Image
                  key={`${provider}-manager-image`}
                  width={24}
                  height={24}
                  src={circleLogoUrlForProvider(provider)}
                  alt={`${provider} icon`}
                />
              )
            })}
          </Stack>
        </Stack>
      }
    />
  )
}
